import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalOrgs", "activeOrgs", "newToday", "canceledToday", "netToday",
    "todayInferences", "peakHour", "peakCount",
    "todayPayments", "todayPaymentAmount",
    "monthPayments", "monthPaymentAmount",
    "todayUsage", "monthUsage",
    "brandTable", "hourlyChart", "topModels",
    "refreshIndicator", "lastUpdated"
  ]

  static values = {
    apiUrl: String
  }

  connect() {
    this.fetchStats()
    this.interval = setInterval(() => this.fetchStats(), 20000)
  }

  disconnect() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  async fetchStats() {
    try {
      this.showRefreshing()
      const response = await fetch(this.apiUrlValue)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      this.updateStats(data)
      this.updateLastUpdated()
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      this.hideRefreshing()
    }
  }

  showRefreshing() {
    if (this.hasRefreshIndicatorTarget) {
      this.refreshIndicatorTarget.classList.add('refreshing')
    }
  }

  hideRefreshing() {
    if (this.hasRefreshIndicatorTarget) {
      setTimeout(() => {
        this.refreshIndicatorTarget.classList.remove('refreshing')
      }, 500)
    }
  }

  updateLastUpdated() {
    if (this.hasLastUpdatedTarget) {
      const now = new Date()
      this.lastUpdatedTarget.textContent = now.toLocaleTimeString()
    }
  }

  updateStats(data) {
    // Organizations
    if (this.hasTotalOrgsTarget) {
      this.totalOrgsTarget.textContent = this.formatNumber(data.organizations?.total || 0)
    }
    if (this.hasActiveOrgsTarget) {
      this.activeOrgsTarget.textContent = this.formatNumber(data.organizations?.total_with_active_plan || 0)
    }
    if (this.hasNewTodayTarget) {
      this.newTodayTarget.textContent = data.organizations?.new_today || 0
    }
    if (this.hasCanceledTodayTarget) {
      this.canceledTodayTarget.textContent = data.organizations?.canceled_today || 0
    }
    if (this.hasNetTodayTarget) {
      const netToday = data.organizations?.net_today || 0
      this.netTodayTarget.textContent = (netToday > 0 ? '+' : '') + netToday
      this.netTodayTarget.className = netToday >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'
    }

    // Inferences
    if (this.hasTodayInferencesTarget) {
      this.todayInferencesTarget.textContent = this.formatNumber(data.inferences?.today_total || 0)
    }

    // Find peak hour
    if (this.hasPeakHourTarget && this.hasPeakCountTarget && data.inferences?.by_hour) {
      const peak = data.inferences.by_hour.reduce((max, h) => h.count > max.count ? h : max, { hour: 0, count: 0 })
      this.peakHourTarget.textContent = `${peak.hour}:00`
      this.peakCountTarget.textContent = this.formatNumber(peak.count)
    }

    // Payments
    if (this.hasTodayPaymentsTarget) {
      this.todayPaymentsTarget.textContent = data.payments?.today?.count || 0
    }
    if (this.hasTodayPaymentAmountTarget) {
      this.todayPaymentAmountTarget.textContent = this.formatCurrency(data.payments?.today?.total_cents || 0)
    }
    if (this.hasMonthPaymentsTarget) {
      this.monthPaymentsTarget.textContent = data.payments?.last_4_weeks?.count || 0
    }
    if (this.hasMonthPaymentAmountTarget) {
      this.monthPaymentAmountTarget.textContent = this.formatCurrency(data.payments?.last_4_weeks?.total_cents || 0)
    }

    // Usage
    if (this.hasTodayUsageTarget) {
      this.todayUsageTarget.textContent = this.formatCurrency(data.usage_spending?.today?.total_cents || 0)
    }
    if (this.hasMonthUsageTarget) {
      this.monthUsageTarget.textContent = this.formatCurrency(data.usage_spending?.last_4_weeks?.total_cents || 0)
    }

    // Brand table
    if (this.hasBrandTableTarget && data.organizations?.by_sign_up_brand) {
      this.updateBrandTable(data.organizations.by_sign_up_brand)
    }

    // Hourly chart
    if (this.hasHourlyChartTarget && data.inferences?.by_hour) {
      this.updateHourlyChart(data.inferences.by_hour)
    }

    // Top models
    if (this.hasTopModelsTarget && data.inferences?.top_models_last_7_days) {
      this.updateTopModels(data.inferences.top_models_last_7_days)
    }
  }

  updateBrandTable(brandData) {
    const rows = Object.entries(brandData).map(([brand, stats]) => {
      const netClass = stats.net_today >= 0 ? 'text-success' : 'text-danger'
      const netSign = stats.net_today > 0 ? '+' : ''
      return `
        <tr>
          <td class="fw-medium">${this.formatBrandName(brand)}</td>
          <td class="text-center">${this.formatNumber(stats.total)}</td>
          <td class="text-center">${this.formatNumber(stats.with_active_plan)}</td>
          <td class="text-center text-success">+${stats.new_today}</td>
          <td class="text-center text-danger">-${stats.canceled_today}</td>
          <td class="text-center ${netClass}">${netSign}${stats.net_today}</td>
        </tr>
      `
    }).join('')
    this.brandTableTarget.innerHTML = rows
  }

  updateHourlyChart(hourlyData) {
    if (hourlyData.length === 0) {
      this.hourlyChartTarget.innerHTML = '<p class="text-muted text-center">No data yet</p>'
      return
    }

    const maxCount = Math.max(...hourlyData.map(h => h.count), 1)
    const barWidth = Math.floor(100 / Math.max(hourlyData.length, 1))

    const bars = hourlyData.map(h => {
      const heightPercent = (h.count / maxCount) * 100
      return `
        <div class="hourly-bar-container" style="width: ${barWidth}%">
          <div class="hourly-bar" style="height: ${heightPercent}%" title="${h.hour}:00 - ${this.formatNumber(h.count)} inferences"></div>
          <div class="hourly-label">${h.hour}</div>
        </div>
      `
    }).join('')

    this.hourlyChartTarget.innerHTML = `<div class="hourly-chart-bars">${bars}</div>`
  }

  updateTopModels(models) {
    if (models.length === 0) {
      this.topModelsTarget.innerHTML = '<tr><td colspan="3" class="text-muted text-center">No data</td></tr>'
      return
    }

    const rows = models.slice(0, 10).map((model, index) => `
      <tr>
        <td>
          <span class="badge bg-secondary me-2">${index + 1}</span>
          ${model.name}
        </td>
        <td class="text-center"><span class="badge bg-light text-dark">${model.type || '-'}</span></td>
        <td class="text-end fw-medium">${this.formatNumber(model.inference_count)}</td>
      </tr>
    `).join('')

    this.topModelsTarget.innerHTML = rows
  }

  formatNumber(num) {
    return new Intl.NumberFormat().format(num)
  }

  formatCurrency(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100)
  }

  formatBrandName(brand) {
    return brand.charAt(0).toUpperCase() + brand.slice(1)
  }
}
